import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import Brand from '../../../../types/brand/Brand';
import BrandWriteDTO from '../../../../types/brand/BrandWriteDTO';
import Page from '../../../../types/shared/Page';
import { IsAdminDirective } from '../../directives/is-admin.directive';
import { BrandService } from '../../services/brand.service';
import { ExportService } from '../../services/export.service';
import { CreateBrandDialogComponent } from '../shared/brands/create-brand-dialog/create-brand-dialog.component';
import { UpdateBrandDialogComponent } from '../shared/brands/update-brand-dialog/update-brand-dialog.component';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

@Component({
    selector: 'app-brands',
    templateUrl: './brands.component.html',
    styleUrls: [ './brands.component.scss' ],
    imports: [
        PageHeaderComponent,
        IconField,
        InputIcon,
        FormsModule,
        TableModule,
        Button,
        SharedModule,
        CreateBrandDialogComponent,
        UpdateBrandDialogComponent,
        InputText,
        RouterLink,
        IftaLabelModule,
        IsAdminDirective,
        Tooltip
    ]
})
export class BrandsComponent implements OnInit {
    public search = '';
    public brands: Brand[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateBrandDialog = false;
    public displayUpdateBrandDialog = false;
    public selectedBrand: BrandWriteDTO = new BrandWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(private brandService: BrandService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                private exportService: ExportService) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadBrands();

        // Debounce the search input to prevent too many requests
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0; // Reset to the first page on new search
            this.loadBrands();
        });
    }

    /**
     * Handles the page change event.
     *
     * @param event The page change event.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadBrands();
    }

    /**
     * Handles the search input change event.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Edits the brand.
     * @param brand The brand to edit.
     */
    public editBrand(brand: Brand): void {
        this.selectedBrand = new BrandWriteDTO(brand);
        this.displayUpdateBrandDialog = true;
    }

    /**
     * Deletes the brand.
     * @param brand The brand to delete.
     */
    public deleteBrand(brand: Brand): void {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete the brand '${ brand.name }'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.brandService.deleteBrand(brand.id).then(() => {
                    this.loadBrands();
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Brand deleted', life: 3000 }
                    );
                }).catch((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Opens the create brand dialog.
     */
    public openCreateBrandDialog(): void {
        this.displayCreateBrandDialog = true;
    }

    /**
     * Reloads the brands.
     */
    public reloadBrands(): void {
        this.loadBrands();
    }

    /**
     * Exports the brands.
     */
    public exportBrands() {
        this.exportService.exportBrands().then(() => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Brands exported', life: 3000 });
        }).catch((error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
        });
    }

    /**
     * Loads the brands.
     */
    private loadBrands(): void {
        this.loading = true;

        const size = this.rows;
        const search = this.search || '';

        this.brandService
            .getBrands(search, this.currentPage, size)
            .then((result: Page<Brand>) => {
                this.brands = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }
}
